import React, { createContext, useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import bousanmSolideApi from '../services/bousanmSolideApi';
import Swal from 'sweetalert2';

const BousanmSolideContext = createContext();

export const useBousanmSolide = () => {
  const context = useContext(BousanmSolideContext);
  if (!context) {
    throw new Error('useBousanmSolide must be used within BousanmSolideProvider');
  }
  return context;
};

export const BousanmSolideProvider = ({ children }) => {
  const { t } = useTranslation(['solide', 'common']);
  // États globaux
  const [globalStats, setGlobalStats] = useState(null);
  const [agentsPortfolio, setAgentsPortfolio] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentTransactions, setAgentTransactions] = useState(null);
  const [assignationsHistory, setAssignationsHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Charger les statistiques globales
   */
  const fetchGlobalStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.getGlobalStats();
      setGlobalStats(data);
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.stats');
      setError(errorMsg);
      Swal.fire({
        title: t('common:error'),
        text: errorMsg,
        icon: 'error',
        toast: true,
        position: 'top-right',
        timer: 3000,
        showConfirmButton: false
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Charger le portfolio des agents
   */
  const fetchAgentsPortfolio = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.getAgentsPortfolio(filters);
      setAgentsPortfolio(data);
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.agents');
      setError(errorMsg);
      Swal.fire({
        title: t('common:error'),
        text: errorMsg,
        icon: 'error',
        toast: true,
        position: 'top-right',
        timer: 3000,
        showConfirmButton: false
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Charger les transactions d'un agent
   */
  const fetchAgentTransactions = useCallback(async (username, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.getAgentTransactions(username, filters);
      setAgentTransactions(data);
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.transactions');
      setError(errorMsg);
      Swal.fire({
        title: t('common:error'),
        text: errorMsg,
        icon: 'error',
        toast: true,
        position: 'top-right',
        timer: 3000,
        showConfirmButton: false
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Assigner un agent à un compte
   */
  const assignAgent = useCallback(async (compteCode, agentUsername, raison = '') => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.assignAgent({
        compte_code: compteCode,
        agent_username: agentUsername,
        raison: raison
      });
      
      Swal.fire({
        title: t('common:success'),
        text: data.message || t('solide:context.messages.assignSuccess'),
        icon: 'success',
        toast: true,
        position: 'top-right',
        timer: 3000,
        showConfirmButton: false
      });
      
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.assign');
      setError(errorMsg);
      Swal.fire({
        title: t('common:error'),
        text: errorMsg,
        icon: 'error',
        confirmButtonText: t('common:ok')
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Désassigner un agent d'un compte
   */
  const unassignAgent = useCallback(async (compteCode, raison = '') => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.unassignAgent({
        compte_code: compteCode,
        raison: raison
      });
      
      Swal.fire({
        title: t('common:success'),
        text: data.message || t('solide:context.messages.unassignSuccess'),
        icon: 'success',
        toast: true,
        position: 'top-right',
        timer: 3000,
        showConfirmButton: false
      });
      
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.unassign');
      setError(errorMsg);
      Swal.fire({
        title: t('common:error'),
        text: errorMsg,
        icon: 'error',
        confirmButtonText: t('common:ok')
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Récupérer l'agent assigné à un compte
   */
  const getCompteAgent = useCallback(async (compteCode) => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.getCompteAgent(compteCode);
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.getAgent');
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Charger l'historique des assignations
   */
  const fetchAssignationsHistory = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await bousanmSolideApi.getAssignationsHistory(filters);
      setAssignationsHistory(data);
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || t('solide:context.errors.history');
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Réinitialiser l'état
   */
  const reset = useCallback(() => {
    setGlobalStats(null);
    setAgentsPortfolio(null);
    setSelectedAgent(null);
    setAgentTransactions(null);
    setAssignationsHistory(null);
    setError(null);
  }, []);

  const value = {
    // État
    globalStats,
    agentsPortfolio,
    selectedAgent,
    agentTransactions,
    assignationsHistory,
    loading,
    error,
    
    // Actions
    fetchGlobalStats,
    fetchAgentsPortfolio,
    fetchAgentTransactions,
    assignAgent,
    unassignAgent,
    getCompteAgent,
    fetchAssignationsHistory,
    setSelectedAgent,
    reset
  };

  return (
    <BousanmSolideContext.Provider value={value}>
      {children}
    </BousanmSolideContext.Provider>
  );
};

export default BousanmSolideContext;
